(() => {
  if (customElements.get('t2s-scoped-sizes')) return;
  const categories = {
    Tops: ['Shirts', 'Tops', 'Sweaters'],
    Bottoms: ['Pants', 'Dress pants', 'Skorts', 'Shorts', 'Jeans', 'Skirts', 'Leggings', 'Tracksuits', 'Rompers', 'Overalls'],
    Outerwear: ['Jackets', 'Vests', 'Coats'],
    Footwear: ['Boots', 'Sneakers', 'Work shoes', 'Dress shoes', 'Loafers', 'Shoes', 'Boat shoes', 'Sandals', 'Clogs & mules', 'Moccasins'],
    Dresses: ['Dresses']
  };
  const departments = { mens: "Men’s", womens: "Women’s", boys: "Boys’", girls: "Girls’", kids: 'Kids’' };
  const order = ['Tops', 'Bottoms', 'Outerwear', 'Footwear', 'Dresses'];
  const sizeOrder = ['XXS', 'XS', 'S', 'S/M', 'M', 'M/L', 'L', 'L/XL', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL'];
  const sorter = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
  function compareSize(a, b) {
    const x = sizeOrder.indexOf(a[0]), y = sizeOrder.indexOf(b[0]);
    return x >= 0 && y >= 0 ? x - y : sorter.compare(a[0], b[0]);
  }
  class ScopedSizes extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      document.addEventListener('shopify:collection:update', () => this.load(), {signal: this.events.signal});
      window.addEventListener('popstate', () => this.load(), {signal: this.events.signal});
      window.addEventListener('pageshow', event => { if (event.persisted) this.load(); }, {signal: this.events.signal});
      this.load();
    }
    async data(url) {
      const output = [];
      const ids = new Set();
      let pages = 1, expected = null;
      for (let page = 1; page <= pages; page++) {
        const endpoint = new URL(url);
        endpoint.searchParams.set('view', 't2s-size-data');
        endpoint.searchParams.set('page', String(page));
        endpoint.searchParams.delete('section_id');
        endpoint.searchParams.delete('sections');
        endpoint.searchParams.set('preview_theme_id', this.dataset.theme);
        const response = await fetch(endpoint, {credentials: 'same-origin', signal: this.abort.signal});
        if (!response.ok) throw new Error('Size data unavailable');
        const text = await response.text();
        const data = JSON.parse(text);
        if (String(data.theme) !== this.dataset.theme || data.page !== page || !Array.isArray(data.products)) throw new Error('Unexpected size data');
        if (page === 1) { pages = data.pages; expected = data.total; }
        if (data.total !== expected || data.pages !== pages || expected > 25000) throw new Error('Collection changed while loading');
        for (const product of data.products) {
          if (ids.has(product.id)) throw new Error('Repeated product page');
          ids.add(product.id);
          output.push(product);
        }
      }
      if (output.length !== expected) throw new Error('Incomplete size data');
      return output;
    }
    async load() {
      this.abort?.abort();
      this.abort = new AbortController();
      const status = this.querySelector('[role="status"]');
      const target = this.querySelector('.t2s-scoped-sizes__groups');
      target.replaceChildren();
      status.textContent = this.dataset.loading;
      try {
        const current = new URL(location.href);
        const genderTag = this.dataset.collection === 'new-in' ? current.pathname.split('/')[3] : '';
        const allGender = ['mens', 'womens'].includes(genderTag);
        const landing = allGender ? [genderTag, genderTag === 'mens' ? 'footwear' : 'womens-footwear'] : { 'mens-apparel': ['mens', 'footwear'], 'womens-apparel': ['womens', 'womens-footwear'] }[this.dataset.collection];
        const showLanding = allGender || landing && !current.pathname.split('/')[3] && ![...current.searchParams.keys()].some(key => key.startsWith('filter.'));
        const landingGroups = landing?.[0] === 'womens' ? ['Tops', 'Bottoms', 'Outerwear', 'Footwear', 'Dresses'] : ['Tops', 'Bottoms', 'Outerwear', 'Footwear'];
        const sources = showLanding
          ? landingGroups.map(group => {
            const handle = group === 'Footwear' ? landing[1] : `${landing[0]}-${group.toLowerCase()}`;
            const url = new URL(current);
            url.pathname = this.dataset.url.replace(/[^/]+$/, handle);
            url.searchParams.delete('page');
            return {url, group, department: landing[0]};
          })
          : [{url: current}];
        const groups = new Map();
        await Promise.all(sources.map(async source => {
          const products = await this.data(source.url);
          const broadGroups = new Set(products.filter(product => product.stock && product.category).map(product => Object.keys(categories).find(key => categories[key].includes(product.category)) || product.category));
          const categoryScoped = !!source.group || broadGroups.size === 1;
          for (const product of products) {
            if (!product.stock || typeof product.size !== 'string' || !product.size.trim() || !product.category) continue;
            const group = Object.keys(categories).find(key => categories[key].includes(product.category)) || product.category;
            if (source.group && source.group !== group) continue;
            const depts = source.department ? [source.department] : [''];
            for (const dept of depts) {
              const displayGroup = categoryScoped ? group : product.category;
              const key = `${dept}|${displayGroup}`;
              if (!groups.has(key)) groups.set(key, {dept, group: displayGroup, categoryScoped, url: source.url, sizes: new Map()});
              const entry = groups.get(key);
              if (!entry.sizes.has(product.size)) entry.sizes.set(product.size, {ids: new Set(), categories: new Set()});
              const size = entry.sizes.get(product.size);
              size.ids.add(product.id);
              size.categories.add(product.category);
            }
          }
        }));
        const fragment = document.createDocumentFragment();
        const entries = [...groups.values()].sort((a,b) => sorter.compare(a.dept,b.dept) || (order.indexOf(a.group)<0?99:order.indexOf(a.group)) - (order.indexOf(b.group)<0?99:order.indexOf(b.group)) || sorter.compare(a.group,b.group));
        for (const entry of entries) {
          const section = document.createElement('section');
          section.className = 't2s-scoped-sizes__group';
          const title = [departments[entry.dept], entry.group].filter(Boolean).join(' ');
          const heading = document.createElement('h3');
          heading.textContent = title;
          section.append(heading);
          const tiles = document.createElement('div');
          tiles.className = 't2s-scoped-sizes__tiles';
          for (const [label, size] of [...entry.sizes.entries()].sort(compareSize)) {
            const url = new URL(entry.url);
            url.searchParams.delete('page');
            url.searchParams.delete('view');
            url.searchParams.delete('section_id');
            url.searchParams.delete('sections');
            url.searchParams.set('filter.p.m.t2s.size', label);
            if (!entry.categoryScoped) url.searchParams.set('filter.p.m.t2s.category', [...size.categories][0]);
            url.searchParams.set('filter.v.availability', '1');
            url.searchParams.set('preview_theme_id', this.dataset.theme);
            if (entry.dept) {
              const path = url.pathname.split('/');
              const tags = path[3] ? path[3].split('+') : [];
              if (!tags.includes(entry.dept)) tags.push(entry.dept);
              path[3] = tags.join('+');
              url.pathname = path.join('/');
            }
            const selected = current.searchParams.getAll('filter.p.m.t2s.size').includes(label) && current.searchParams.get('filter.p.m.t2s.category') === url.searchParams.get('filter.p.m.t2s.category');
            if (selected) {
              url.href = current.href;
              url.searchParams.delete('filter.p.m.t2s.size');
              url.searchParams.delete('page');
              url.searchParams.delete('section_id');
              url.searchParams.delete('sections');
              url.searchParams.set('preview_theme_id', this.dataset.theme);
            }
            const link = document.createElement('a');
            link.className = 't2s-scoped-sizes__tile';
            link.href = url.pathname + url.search;
            link.setAttribute('aria-label', `${title}, ${this.dataset.size} ${label}, ${size.ids.size} ${this.dataset.stock}${selected ? `, ${this.dataset.clear}` : ''}`);
            const text = document.createElement('span'); text.textContent = label;
            const count = document.createElement('span'); count.textContent = `${size.ids.size} ${this.dataset.stock}`;
            link.append(text,count);
            if (selected) link.setAttribute('aria-current','page');
            tiles.append(link);
          }
          section.append(tiles); fragment.append(section);
        }
        target.replaceChildren(fragment);
        status.textContent = groups.size ? '' : this.dataset.empty;
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = this.dataset.error;
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.events?.abort(); }
  }
  customElements.define('t2s-scoped-sizes', ScopedSizes);
})();
